import type {NotificationModel} from 'app/models/NotificationModel'
import {buildNotificationLink} from 'common/helpers/notifications'

/*
 * Where a student/parent notification opens — the mobile app's routing
 * (screens/commonScreens/Notifications: `navigateToRoute` +
 * `UPDATED_NOTIFICATIONS_ROUTES`), translated to the portal's native routes.
 * Mobile picks the screen by the notification's type (`module`) / objectType;
 * only types it has no screen for follow the stored `webUrl`, as here.
 */

/** What the resolver needs to know about the viewer. */
export type NotificationLinkContext = {
  can: (permission: string) => boolean
  /** Lyceum hides the exam pages from students/parents. */
  lyceum: boolean
  /** Fee section path for the tenant. */
  feePath: string
}

type Target =
  | 'homework'
  | 'lecture'
  | 'news'
  | 'attendance'
  | 'subjectAttendance'
  | 'amendments'
  | 'digitalRequests'
  | 'tests'
  | 'exams'
  | 'dailySchedule'
  | 'yearlySchedule'
  | 'fee'

/** Notification type (`module`) → mobile screen, family types only. */
const MODULE_TARGET: Record<string, Target> = {
  homewrok_notify: 'homework',
  homework_result: 'homework',
  student_present_notify: 'attendance',
  student_absent_notify: 'attendance',
  student_leave_notify: 'attendance',
  student_late_notify: 'attendance',
  student_attendance_warning: 'attendance',
  student_checkout_notify: 'attendance',
  student_early_checkout_notify: 'attendance',
  student_subj_present_notify: 'subjectAttendance',
  student_subj_absent_notify: 'subjectAttendance',
  student_subj_leave_notify: 'subjectAttendance',
  student_subj_bunk_notify: 'subjectAttendance',
  student_subj_late_notify: 'subjectAttendance',
  student_subj_attendance_warning: 'subjectAttendance',
  student_attendance_amendment_request_submitted: 'amendments',
  student_attendance_amendment_request_approved: 'amendments',
  student_attendance_amendment_request_rejected: 'amendments',
  digital_request_received: 'digitalRequests',
  digital_request_approved: 'digitalRequests',
  digital_request_rejected: 'digitalRequests',
  class_test_notify: 'tests',
  test_result: 'tests',
  test_result_absent: 'tests',
  test_attempted_notif: 'tests',
  test_unattempted_notif: 'tests',
  exam_attempted_notif: 'exams',
  exam_unattempted_notif: 'exams',
  exam_notification: 'exams',
  exam_result: 'exams',
  daily_schedule: 'dailySchedule',
  yearly_schedule: 'yearlySchedule',
  news_notify: 'news',
  fee_payment: 'fee',
  fee_remainder_before_due_date: 'fee',
  fee_remainder_after_due_date: 'fee',
  fee_collection_voucher: 'fee',
}

/** Trailing number of the stored url (mobile `extractIdFromUrl`). */
export const extractIdFromUrl = (webUrl: string | null | undefined) => {
  const match = /(\d+)\/?$/.exec((webUrl ?? '').split(/[?#]/)[0])
  return match ? match[1] : ''
}

/**
 * `?date=…` of the stored url (the attendance notifications name the day),
 * kept so the report opens on that day.
 */
const dateQuery = (webUrl: string | null | undefined) => {
  const match = /[?&]date=([^&#]+)/.exec(webUrl ?? '')
  return match ? `?date=${match[1]}` : ''
}

const canAttendanceReport = (can: NotificationLinkContext['can']) =>
  can('attendance_manager_student_attendance_report') && can('attendance_manager_view')

/**
 * In-app link of a family notification, chosen like the mobile app: homework
 * notifications open the homework list, lectures the lecture, news the news
 * item, attendance the (subject) attendance report of that day, amendment and
 * digital requests their lists, tests / exams their lists, schedules the
 * calendars, fee reminders the fee page. A type mobile has no screen for —
 * or one whose page the viewer cannot open — follows the stored `webUrl`
 * (`'#'` when there is none).
 * @param row the notification
 * @param ctx viewer permissions and tenant flags
 */
export const getFamilyNotificationLink = (
  row: Pick<NotificationModel, 'module' | 'objectType' | 'webUrl'>,
  ctx: NotificationLinkContext
): string => {
  const {can, lyceum, feePath} = ctx
  const fallback = buildNotificationLink(row.webUrl, false)
  const id = extractIdFromUrl(row.webUrl)

  // Mobile checks these two before the type table.
  if (row.objectType === 'HOMEWORK' || row.module === 'homewrok_notify') {
    return can('homework_manager') ? '/homework/admin' : fallback
  }
  if (row.objectType === 'LECTURE') {
    if (!can('video_lecture')) return fallback
    return id ? `/institute/lectures-new/${id}` : '/institute/lectures-new/list'
  }

  switch (MODULE_TARGET[row.module ?? '']) {
    case 'homework':
      return can('homework_manager') ? '/homework/admin' : fallback
    case 'news':
      if (!can('news_manager')) return fallback
      return id ? `/institute/news/${id}` : '/institute/news/fullmode'
    case 'attendance':
      return canAttendanceReport(can)
        ? `/attendance/studentAttendReport${dateQuery(row.webUrl)}`
        : fallback
    case 'subjectAttendance':
      return can('attendance_manager_student_subject_attendance_report') &&
        can('attendance_manager_view')
        ? `/attendance/studentSubjectAttendReport${dateQuery(row.webUrl)}`
        : fallback
    case 'amendments':
      return canAttendanceReport(can) ? '/attendance/viewRequests' : fallback
    case 'digitalRequests':
      return can('digital_request_manager') ? '/institute/digital-requests/list' : fallback
    case 'tests':
      return can('test_manager') ? '/test/admin' : fallback
    case 'exams':
      return !lyceum && can('exam_manager_exams') ? '/exams' : fallback
    case 'dailySchedule':
      return '/institute/schedule/daily-calendar'
    case 'yearlySchedule':
      return '/institute/schedule/yearly-calendar'
    case 'fee':
      return can('fee_manager') ? feePath : fallback
    default:
      return fallback
  }
}
